import {Button} from "@material-ui/core";
import BulletinsDocument from 'Agent/AgentForms/Bulletins/components/BulletinsDocuments';
import BulletinSearchForm from "Agent/AgentForms/Bulletins/components/BulletinSearchForm";
import {
    changeBulletinsFilters,
    changeSort,
    clearBulletins,
    clearSort,
    FilterEnum,
    getBulletins
} from 'Agent/AgentForms/Bulletins/ducks/Bulletin';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import * as O from "fp-ts/lib/Option";
import React, { useState , useEffect} from "react";
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import Column from 'Shared/components/PagedView/Column';
import {
    withDateFilterProperties,
    withSelectFilterProperties,
    withStringFilterProperties
} from "Shared/components/PagedView/Filter";
import PagedView from 'Shared/components/PagedView/PagedView';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {addFilterParametersWithDatesFormatted} from "Shared/utilities/addFilterParameters";
import {toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {withValueChangedHook} from "Shared/utilities/withValueChanged";

const Channels: FilterEnum[] = [
    {
        name: 'Web Only',
        value: 'Web Only',
    }, {
        name: 'Allstate Agents',
        value: 'Allstate Agents',
    }, {
        name: 'Non-Allstate Agents',
        value: 'Non-Allstate Agents',
    }, {
        name: 'Both',
        value: 'Both',
    },
];

export const BulletinTypes: FilterEnum[] = [
    {
        name: 'Administrative',
        value: 'Administrative',
    }, {
        name: 'Announcement',
        value: 'Announcement',
    }, {
        name: 'Product',
        value: 'Product',
    }, {
        name: 'Technology',
        value: 'Technology',
    },
];

type BulletinsProps = {
    selectedAgency: AgencyFilters;
} & RouteComponentProps;

const Bulletins = (props: BulletinsProps) => {
    const dispatch = useDispatch();
    useBreadcrumbs("Agency Bulletins", props.match.url);
    const {selectedAgency} = props;
    const {bulletins, filters: bulletinFilters, sort} = useSelector((state: ReducerState) => state.bulletins);
    const changeFilter = withValueChangedHook(bulletinFilters, changeBulletinsFilters, dispatch);
    const [isSearchBlock, setIsSearchBlock] = useState(false);

    const filterProperties = {
        changeFilter,
        filterData: bulletinFilters,
    };

    useEffect(() => {
        dispatch(
            changeBulletinsFilters({...bulletinFilters})
        );
    }, []);

    useEffect(() => {
        bulletinFilters.pageNumber = "1";
        dispatch(
            getBulletins(
                undefined,
                bulletinFilters,
                O.toUndefined(sort),
                selectedAgency.isNewYork
            )
        );
    }, [bulletinFilters, sort, selectedAgency.isNewYork]);

    useEffect(() => {
        bulletinFilters.pageNumber = "1";
        return () => {
            dispatch(clearBulletins({...bulletinFilters}));
            dispatch(clearSort());
        };
    }, [clearBulletins, changeBulletinsFilters]);

    const onClickSearchForm = () => {
        setIsSearchBlock(!isSearchBlock);
    }

    return (
        <>
            <div className="backdrop-container" id="backdrop" style={{display: isSearchBlock ? 'block' : 'none'}}/>
        <PagedView
                pagedViewKey="bulletins"
                sort={O.toUndefined(sort)}
                onChangeSort={sort => dispatch(changeSort(sort))}
                data={bulletins}
                loadMore={page => {
                    const nextPage: number = parseInt(bulletinFilters.pageNumber) + 1;
                    if (page.totalCount > page.result.length) {
                        bulletinFilters.pageNumber = nextPage.toString();
                        let sortValue = (sort._tag === 'Some') ? '&sortColumn=' + sort.value.column + '&sortOrder=' + sort.value.direction : '';
                        let link;
                        if(sortValue !== '')
                        {
                            link =  addFilterParametersWithDatesFormatted(bulletinFilters) + '&isNewYork=' + selectedAgency.isNewYork + sortValue;
                        }
                        else {
                            link =addFilterParametersWithDatesFormatted(bulletinFilters) + '&isNewYork=' + selectedAgency.isNewYork;
                        }
                        page.links = [{"href": "api/documents/v1/bulletins"+ link, "method": "GET", "rel": "nextpage" }]
                    }
                    dispatch(
                        getBulletins(
                            page,
                            bulletinFilters,
                            O.toUndefined(sort),
                            selectedAgency.isNewYork)
                    );
                }}
                actions={<>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={onClickSearchForm}
                    > SEARCH FORMS
                    </Button>
                </>
                }
            >
                {columnProps => (
                    <>
                        <Column
                            {...columnProps}
                            title="Created On"
                            sortColumnKey="createdOn"
                            filterProperties={withDateFilterProperties(filterProperties, 'created')}
                            default
                        >
                            {bulletin => toFormattedDateAsUTC(bulletin.createdOn)}
                        </Column>
                        <Column
                            {...columnProps}
                            title="Type"
                            sortColumnKey="type"
                            filterProperties={withSelectFilterProperties(filterProperties, 'bulletinType', BulletinTypes)}
                            default
                        >
                            {bulletin => bulletin.type}
                        </Column>
                        <Column
                            {...columnProps}
                            title="Subject"
                            sortColumnKey="subject"
                            filterProperties={withStringFilterProperties(filterProperties, 'bulletinSubject')}
                            default
                        >
                            {bulletin => bulletin.subject}
                        </Column>
                        <Column
                            {...columnProps}
                            title="Link"
                            default
                        >
                            {bulletin =>
                                <BulletinsDocument
                                    bulletin={bulletin}
                                    selectedAgency={selectedAgency}
                                />
                            }
                        </Column>
                        <Column
                            {...columnProps}
                            title="Emailed On"
                            sortColumnKey="emailedOn"
                            filterProperties={withDateFilterProperties(filterProperties, 'emailed')}
                            default
                        >
                            {bulletin => toFormattedDateAsUTC(bulletin.emailedOn)}
                        </Column>
                        <Column
                            {...columnProps}
                            title="Channel"
                            sortColumnKey="channels"
                            filterProperties={withSelectFilterProperties(filterProperties, 'channels', Channels)}
                            default
                        >
                            {bulletin => bulletin.channels}
                        </Column>

                    </>
                )}
            </PagedView>
            {isSearchBlock &&
            <BulletinSearchForm
                formName='Search Bulletins'
                setIsSearchBlock={setIsSearchBlock}
                filters={bulletinFilters}
                filter={changeBulletinsFilters}
            />
            }
        </>
    )
};

export default Bulletins;
